import { useTranslation } from "react-i18next";
import { Settings } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DescriptionSummary } from "./DescriptionSummary";
import { FlagSelect } from "./FlagSelect";
import { StepFooter } from "./StepFooter";

interface SettingsStepProps {
  description: string;
  maxResults: number;
  onMaxResultsChange: (val: number) => void;
  language: string;
  onLanguageChange: (val: string) => void;
  countryCode: string;
  onCountryCodeChange: (val: string) => void;
  onBack: () => void;
  onContinue: () => void;
  onSkipToReview: () => void;
}

export function SettingsStep({
  description,
  maxResults,
  onMaxResultsChange,
  language,
  onLanguageChange,
  countryCode,
  onCountryCodeChange,
  onBack,
  onContinue,
  onSkipToReview,
}: SettingsStepProps) {
  const { t } = useTranslation("search");

  // Module-level const arrays moved inside component so entries can call t()
  const LANGUAGE_OPTIONS = [
    { code: "de", label: `${t("wizard.reviewStep.languages.de")} (de)` },
    { code: "en", label: `${t("wizard.reviewStep.languages.en")} (en)` },
    { code: "fr", label: `${t("wizard.reviewStep.languages.fr")} (fr)` },
  ];

  const COUNTRY_OPTIONS = [
    { code: "de", label: `${t("wizard.reviewStep.countries.de")} (de)` },
    { code: "at", label: `${t("wizard.reviewStep.countries.at")} (at)` },
    { code: "ch", label: `${t("wizard.reviewStep.countries.ch")} (ch)` },
    { code: "fr", label: `${t("wizard.reviewStep.countries.fr")} (fr)` },
    { code: "gb", label: `${t("wizard.reviewStep.countries.gb")} (gb)` },
  ];

  return (
    <div className="space-y-4">
      <DescriptionSummary description={description} />

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings className="h-4 w-4 text-primary" />
            {t("wizard.settingsStep.cardTitle")}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Max Results */}
          <div className="space-y-1.5">
            <label
              htmlFor="max-results"
              className="text-xs font-medium text-foreground"
            >
              {t("wizard.settingsStep.maxResultsLabel")}
            </label>
            <Input
              id="max-results"
              type="number"
              min={1}
              max={200}
              value={maxResults}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!isNaN(n))
                  onMaxResultsChange(Math.max(1, Math.min(200, n)));
              }}
            />
            <p className="text-xs text-muted-foreground">
              {t("wizard.settingsStep.maxResultsHint")}
            </p>
          </div>

          {/* Language + Country */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                {t("wizard.settingsStep.languageLabel")}
              </label>
              <FlagSelect
                value={language}
                onChange={onLanguageChange}
                options={LANGUAGE_OPTIONS}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                {t("wizard.settingsStep.countryLabel")}
              </label>
              <FlagSelect
                value={countryCode}
                onChange={onCountryCodeChange}
                options={COUNTRY_OPTIONS}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <StepFooter
        onBack={onBack}
        onContinue={onContinue}
        onSkipToReview={onSkipToReview}
      />
    </div>
  );
}
